/**
 * 检查权限是否匹配通配符模式
 * 例如：pms:task:watcher:view 匹配 pms:*:* 或 pms:task:*
 */
export function matchesWildcard(permissionCode: string, wildcardPattern: string): boolean {
  const prefix = wildcardPattern.split('*')[0]
  if (!prefix) return false
  const normalizedPrefix = prefix.endsWith(':') ? prefix : prefix + ':'
  return permissionCode.startsWith(normalizedPrefix)
}

/** 权限项最小接口 */
export interface PermissionItemLike {
  id: string
  code: string
}

/** 菜单项最小接口 */
export interface MenuItemLike {
  id: string | number
  name?: string
  permission?: string[] | string | null
  type?: number
  children?: MenuItemLike[]
}

/**
 * 从菜单项中提取权限通配符列表
 * 兼容 jsonb 数组（string[]）和旧的单字符串格式
 */
function getMenuPermissions(menu: MenuItemLike): string[] {
  if (!menu.permission) return []
  if (Array.isArray(menu.permission)) return menu.permission
  if (typeof menu.permission === 'string' && menu.permission.length > 0) return [menu.permission]
  return []
}

/**
 * 从菜单树中收集所有菜单的 ID
 */
function collectAllMenuIds(menus: MenuItemLike[]): Set<string> {
  const ids = new Set<string>()
  function walk(items: MenuItemLike[]) {
    for (const item of items) {
      ids.add(String(item.id))
      if (item.children?.length) walk(item.children)
    }
  }
  walk(menus)
  return ids
}

/**
 * 根据选中的菜单计算所需权限 ID 列表
 *
 * 匹配逻辑：
 * 1. 全选菜单 → 返回所有非通配符权限 ID
 * 2. 从选中菜单的 permission 字段收集通配符模式（数据库驱动，不再硬编码）
 * 3. 从全量权限列表中匹配所有通配符，返回权限 ID 列表
 */
export function computePermissionsByMenus(
  selectedMenuIds: string[],
  allMenus: MenuItemLike[],
  allPermissions: PermissionItemLike[],
): string[] {
  if (selectedMenuIds.length === 0) return []

  // 1. 全选检查
  const allIds = collectAllMenuIds(allMenus)
  const isAllSelected = allIds.size > 0 && selectedMenuIds.length >= allIds.size
  if (isAllSelected) {
    return allPermissions.filter(p => !p.code.includes('*')).map(p => p.id)
  }

  // 2. 扁平化菜单树
  const menuMap = new Map<string, MenuItemLike>()
  function indexMenus(items: MenuItemLike[]) {
    for (const item of items) {
      menuMap.set(String(item.id), item)
      if (item.children?.length) indexMenus(item.children)
    }
  }
  indexMenus(allMenus)

  // 3. 从选中菜单的 permission 字段收集通配符模式
  const wildcardPatterns = new Set<string>()
  for (const menuId of selectedMenuIds) {
    const menu = menuMap.get(menuId)
    if (!menu) continue
    for (const p of getMenuPermissions(menu)) {
      wildcardPatterns.add(p)
    }
  }

  if (wildcardPatterns.size === 0) return []

  // 4. 匹配权限
  const matchedIds: string[] = []
  for (const perm of allPermissions) {
    if (perm.code.includes('*')) continue
    for (const pattern of wildcardPatterns) {
      if (matchesWildcard(perm.code, pattern)) {
        matchedIds.push(perm.id)
        break
      }
    }
  }

  return matchedIds
}
